/**
 * React components for text formatting utilities
 * These components use the centralized TextFormattingUtils and render JSX
 */
import React from "react";
import { COLORS, FONT_SIZES, SPACING, getBulletPointData } from "./TextFormattingUtils.js";

/**
 * Render bullet points for descriptions (experience, projects, etc.)
 * @param {string} description - Description text
 * @param {Object} options - Rendering options
 * @returns {React.Element|null} - Rendered bullet points
 */
export const renderDescriptionBullets = (description, options = {}) => {
  const data = getBulletPointData(description, options);
  if (!data) return null;

  const { points, styles } = data;

  return (
    <div style={styles.container}>
      {points.map((point, index) => (
        <div key={index} style={styles.bulletLine}>
          <span style={styles.bulletSymbol}>•</span>
          <span style={styles.bulletText}>{point}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Render bullet points for achievements
 * @param {string} description - Achievement description
 * @param {Object} options - Rendering options
 * @returns {React.Element|null} - Rendered achievement bullets
 */
export const renderAchievementBullets = (description, options = {}) => {
  const achievementOptions = {
    fontSize: FONT_SIZES.BODY,
    color: COLORS.SECONDARY,
    bulletColor: "#1890ff", // Blue bullets for achievements
    containerStyles: {
      backgroundColor: options.highlight ? "rgba(24, 144, 255, 0.02)" : "transparent",
      padding: options.highlight ? "8px" : "0",
      borderRadius: options.highlight ? "4px" : "0",
      border: options.highlight ? "1px solid rgba(24, 144, 255, 0.1)" : "none",
    },
    ...options,
  };

  return renderDescriptionBullets(description, achievementOptions);
};

/**
 * Render bullet points for projects
 * @param {string} description - Project description
 * @param {Object} options - Rendering options
 * @returns {React.Element|null} - Rendered project bullets
 */
export const renderProjectBullets = (description, options = {}) => {
  const projectOptions = {
    fontSize: FONT_SIZES.BODY,
    color: COLORS.SECONDARY,
    bulletColor: "#52c41a", // Green bullets for projects
    ...options,
  };

  return renderDescriptionBullets(description, projectOptions);
};

// Export aliases for backward compatibility
export const formatDescriptionBullets = renderDescriptionBullets;
export const formatAchievementBullets = renderAchievementBullets;
export const formatProjectBullets = renderProjectBullets;

export default {
  renderDescriptionBullets,
  renderAchievementBullets,
  renderProjectBullets,
  formatDescriptionBullets,
  formatAchievementBullets,
  formatProjectBullets,
};
